import type {FormInputElement} from "../components/formInputElement";
import type {InputType} from "../commons";
import type {DateRangeConfig} from "./dateRange";
import type {EmailValidationConfig} from "./email";
import type {FileSizeConfig} from "./maxUploadSize";
import type {FileCountConfig} from "./maxUploads";
import type {MaxLengthConfig} from "./maxLength";
import type {MinAgeConfig} from "./minAge";
import type {MinCheckedOptionsConfig} from "./minCheckedOptions";
import type {PermittedValuesConfig} from "./permittedValues";
import type {PhoneNumberValidationConfig} from "./phoneNumber";
import type {PostalCodeValidationConfig} from "./postalCode";
import type {RequiredValidationConfig} from "./required";
import type {SupportedFileTypesConfig} from "./supportedFileTypes";
import type {ValidDateConfig} from "./validDate";

export abstract class Validation {
    protected constructor(
        public readonly errorText: string,
        protected warningLevel: WarningLevel
    ) {
    }

    public checkValidity(value: unknown): ValidationResponse {
        return {valid: this.valid(value), warningLevel: this.warningLevel};
    }

    protected abstract valid(value: unknown): boolean;

    public abstract accept<T extends InputType>(inputElement: FormInputElement<T>, value?: T): void ;
}

export type ValidationResponse = {
    valid: boolean;
    warningLevel: WarningLevel;
};

export enum WarningLevel {
    WARNING = "warning",
    ERROR = "error"
}

export class ValidationError extends Error {

    public constructor(public invalidElement: FormInputElement<any>) {
        super("validation error");
    }

}

export enum InputElementType {
    NUMBER = "number",
    TEXT = "text",
    EMAIL = "email",
    PHONE = "tel",
    DATE = "date"
}

export type ValidationConfig = {
    id: string;
    message: string;
    warningLevel: WarningLevel;
};

export type FormValidationConfig =
    | RequiredValidationConfig
    | DateRangeConfig
    | ValidDateConfig
    | MinAgeConfig
    | EmailValidationConfig
    | PostalCodeValidationConfig
    | PhoneNumberValidationConfig
    | MaxLengthConfig
    | FileSizeConfig
    | FileCountConfig
    | SupportedFileTypesConfig
    | PermittedValuesConfig
    | MinCheckedOptionsConfig;